import { publish } from 'pubsub-js';
import { config } from '@/config';

/**
 * Feature permission names - add new features here
 */
export const FEATURES = {
  PRINT_CONTACT_VIEW: 'print_contact_view',
  EXPORT_CONTACTS: 'export_contacts',
  DELETE_CONTACTS: 'delete_contacts',
  TRANSFER_CONTACTS: 'transfer_contacts',
  VIEW_DOWNLINE_CONTACTS: 'view_downline_contacts',
  MAP_VIEW: 'map_view',
  POLICY_OWNERSHIP_INTEL: 'policy_ownership_intel',
  RELATIONSHIP_MANAGEMENT: 'relationship_management',
  SOA: 'soa',
  SMS_MARKETING: 'sms_marketing',
  DRIP_CAMPAIGNS: 'drip_campaigns',
  CUSTOM_SHARED_COMPANY_BRANDING: 'custom_shared_company_branding',
  MANAGER_ROLES_AND_PERMISSIONS: 'manager_roles_and_permissions',
  SHARED_CONTACTS: 'shared_contacts',
  SHARED_CALENDARS: 'shared_calendars',
  SHARED_POLICIES: 'shared_policies',
  ADDONS: 'addons',
  USERS_MANAGEMENT: 'users_management',
  AUTO_APPROVE_AI_EMAIL: 'auto_approve_ai_email',
  BILLING_INFO: 'billing_info',
  IMPERSONATION: 'impersonation',
  WRITING_AGENT: 'writing_agent',
  FAVORITE_LISTS: 'favorite_lists',
  MANAGE_WALLET_BALANCES: 'manage_wallet_balances',
  MANAGE_COMPANY_DOMAIN: 'manage_company_domain',
  CASE_SUBMISSION: 'case_submission',
  FIRE_LIGHT: 'fire_light',
  CSG: 'csg',
  // Addons
  RX_MANAGEMENT: 'rx_management',
  API_ACCESS: 'api_access',
  PREMIUM_INTEGRATIONS: 'premium_integrations',
  MAX_PLUS: 'max_plus'
};

/**
 * Feature display names for the upgrade modal
 */
export const FEATURE_NAMES = {
  [FEATURES.PRINT_CONTACT_VIEW]: 'Print Contact View',
  [FEATURES.EXPORT_CONTACTS]: 'Download Contacts',
  [FEATURES.DELETE_CONTACTS]: 'Delete Contacts',
  [FEATURES.TRANSFER_CONTACTS]: 'Transfer Contacts',
  [FEATURES.VIEW_DOWNLINE_CONTACTS]: 'View Downline Contacts',
  [FEATURES.MAP_VIEW]: 'Map View',
  [FEATURES.POLICY_OWNERSHIP_INTEL]: 'Product Info',
  [FEATURES.RELATIONSHIP_MANAGEMENT]: 'Relationship Management',
  [FEATURES.SOA]: 'AEP - SOA',
  [FEATURES.SMS_MARKETING]: 'SMS Marketing',
  [FEATURES.DRIP_CAMPAIGNS]: 'Drip Campaigns',
  [FEATURES.CUSTOM_SHARED_COMPANY_BRANDING]: 'Custom Shared Company Branding',
  [FEATURES.MANAGER_ROLES_AND_PERMISSIONS]: 'Manager Roles and Permissions',
  [FEATURES.SHARED_CONTACTS]: 'Shared Contacts',
  [FEATURES.SHARED_CALENDARS]: 'Shared Calendars',
  [FEATURES.SHARED_POLICIES]: 'Shared Policies',
  [FEATURES.RX_MANAGEMENT]: 'RX Management',
  [FEATURES.API_ACCESS]: 'API Access',
  [FEATURES.PREMIUM_INTEGRATIONS]: 'Premium Integrations',
  [FEATURES.MAX_PLUS]: 'Max Plus',
  [FEATURES.ADDONS]: 'Addons',
  [FEATURES.AUTO_APPROVE_AI_EMAIL]: 'Auto Approve AI Email',
  [FEATURES.USERS_MANAGEMENT]: 'Users Management',
  [FEATURES.BILLING_INFO]: 'Billing Info',
  [FEATURES.IMPERSONATION]: 'Impersonation',
  [FEATURES.WRITING_AGENT]: 'Writing Agent',
  [FEATURES.FAVORITE_LISTS]: 'Favorite Lists',
  [FEATURES.MANAGE_WALLET_BALANCES]: 'Manage Wallet Balances',
  [FEATURES.MANAGE_COMPANY_DOMAIN]: 'Manage Company Domain',
  [FEATURES.CASE_SUBMISSION]: 'New Case Submission',
  [FEATURES.FIRE_LIGHT]: 'FireLight',
  [FEATURES.CSG]: 'CSG'
};

const ADDON_FEATURES = [
  FEATURES.RX_MANAGEMENT,
  FEATURES.API_ACCESS,
  FEATURES.PREMIUM_INTEGRATIONS,
  FEATURES.MAX_PLUS,
  FEATURES.AUTO_APPROVE_AI_EMAIL,
  FEATURES.USERS_MANAGEMENT
];

export const isAddonFeature = (featureKey, currentUser = null) => {
  const isAddon = ADDON_FEATURES.includes(featureKey);

  if (currentUser) {
    const hasAddonsPermission = currentUser?.permissions?.[FEATURES.ADDONS]?.active;
    return isAddon && hasAddonsPermission;
  }

  return isAddon;
};

/**
 * Check if a feature is accessible for the user
 * @param {Object} permissions - User's permissions object
 * @param {string} featureKey - The feature key to check
 * @returns {Object} - { active: boolean, grayedOut: boolean }
 */
export const checkFeatureAccess = (permissions, featureKey) => {
  if (!config().EXTENDED_PLAN) {
    return { active: true, grayedOut: false };
  }

  const feature = permissions?.[featureKey];

  if (!feature) {
    // If permission not defined, allow access (backward compatibility)
    return { active: true, grayedOut: false };
  }

  return {
    active: feature.active === true,
    grayedOut: feature.gray_out === true
  };
};

/**
 * Handle feature click - checks permission and shows upgrade modal if needed
 * @param {Object} permissions - User's permissions object
 * @param {string} featureKey - The feature key to check
 * @param {Function} onActive - Callback to execute if feature is active
 * @param {Object} options - Optional configuration
 * @param {string} options.customMessage - Custom message for the upgrade modal
 * @returns {boolean} - Returns true if action was active, false if blocked
 */
export const handleFeatureClick = (permissions, featureKey, onActive, options = {}) => {
  if (!config().EXTENDED_PLAN) {
    return onActive && onActive();
  }

  const { active, grayedOut } = checkFeatureAccess(permissions, featureKey);

  if (active) {
    if (typeof onActive === 'function') {
      onActive();
    }
    return true;
  }

  if (grayedOut) {
    const featureName = FEATURE_NAMES[featureKey] || featureKey;
    publish('upgrade_modal', {
      featureName,
      featureKey,
      message:
        options.customMessage ||
        (isAddonFeature(featureKey)
          ? `Buy ${featureName} addon to have access to this feature`
          : `Upgrade your plan to access ${featureName}`)
    });
  }

  return false;
};

/**
 * Show upgrade modal directly
 * @param {string} featureName - Name of the feature to display
 * @param {string} message - Optional custom message
 * @param {string} featureKey - Optional feature key to determine if it's an addon
 */
export const showUpgradeModal = (featureName, message, featureKey) => {
  publish('upgrade_modal', {
    featureName,
    featureKey,
    message
  });
};
